import {
  IAddOpponentToCallParams,
  IEventListItem,
} from '@gem/call/CallController/CallController.types';
import { ProviderMediaType } from '@gem/call/CallProviders/callProviders.constants';
import { IProviderOpponentData, ProviderUid } from '@gem/call/CallProviders/callProviders.types';
import { Call } from '@gem/call/CallStore/Call';
import { Opponent } from '@gem/call/CallStore/Opponent/Opponent';
import * as callModel from '@gem/call/Model/callModel';
import { CallMemberStatus, FinishCallReason } from '@gem/call/Model/callModel.types';
import { CallEventTypes, ClientRole } from '@gem/call/constants';
import { logger } from '@gem/modules/logger';

interface ICallUsersServiceHandlers {
  changeVideoStreamQuality(uid: ProviderUid, isLowQuality: boolean): void;
  addEvent(event: IEventListItem): void;
  getCallMembersByUid(channelId: string, ids: ProviderUid[]): Promise<void>;
}

interface CallUsersServiceConstructorHandlers {
  handlers: ICallUsersServiceHandlers;
  call: Call;
}

export class CallUsersService {
  handlers: ICallUsersServiceHandlers;
  call: Call;

  constructor(params: CallUsersServiceConstructorHandlers) {
    this.call = params.call;
    this.handlers = params.handlers;
  }

  onUserMediaChangedHandler(uid: ProviderUid, media: ProviderMediaType, value: boolean): void {
    const opponent = this.call.opponents.getOpponent(uid);

    if (!opponent) {
      return;
    }

    if (media === ProviderMediaType.Audio && value && this.call.viewState.isWaitingAudioShow) {
      this.call.viewState.setIsWaitingAudioButtonShow(true);
    }

    if (media === ProviderMediaType.Audio) {
      opponent.setHasAudio(value);
    } else if (media === ProviderMediaType.Video) {
      opponent.setHasVideo(value);
      this.updateResolution();
    }
  }

  onUserVolumeLevelChangedHandler(uid: ProviderUid, value: number): void {
    const opponent = this.call.opponents.getOpponent(uid);

    if (!opponent) {
      return;
    }

    opponent.setVolumeLevel(value);
  }

  async onUserJoined(providerOpponentData: IProviderOpponentData): Promise<void> {
    if (providerOpponentData.hasAudio && this.call.viewState.isWaitingAudioShow) {
      this.call.viewState.setIsWaitingAudioButtonShow(true);
    }

    this.addOpponentToCall({
      uid: providerOpponentData.uid,
      hasAudio: providerOpponentData.hasAudio,
      hasVideo: providerOpponentData.hasVideo,
      opponentRole: ClientRole.Host,
    });

    if (providerOpponentData.hasVideo) {
      this.updateResolution();
    }

    let memberDevice = this.call.members.getMemberDeviceByUid(providerOpponentData.uid);

    if (!memberDevice) {
      await this.handlers.getCallMembersByUid(this.call.channelId!, [providerOpponentData.uid]);
      memberDevice = this.call.members.getMemberDeviceByUid(providerOpponentData.uid);
    }

    if (
      memberDevice &&
      typeof providerOpponentData.isHandRaised !== 'undefined' &&
      typeof providerOpponentData.handRaisedAt !== 'undefined'
    ) {
      memberDevice.setHandRaised(
        providerOpponentData.isHandRaised,
        providerOpponentData.handRaisedAt
      );
    }
  }

  onUserLeft(uid: number): void {
    const screenSharing = this.call.opponents.screenSharingOpponent;

    if (uid === screenSharing?.uid) {
      this.call.opponents.deleteOpponent(uid);
      return;
    }

    const opponent = this.call.opponents.getOpponent(uid);

    if (opponent) {
      opponent.setRole(ClientRole.Audience);
      opponent.hasLeft = true;
      this.checkUserLeftAndDelete(opponent.uid);
    }
  }

  async onLeave(params: { channelId: string }): Promise<void> {
    try {
      if (!this.call.isDisposing) {
        await callModel.finishCall(params.channelId, FinishCallReason.Left);
      }
    } catch (e) {
      logger.get('CallController').error('CallUserCallbackService.ToggleHandService.leave', e);
    }
  }

  checkUserLeftAndDelete(uid: ProviderUid): void {
    const opponent = this.call.opponents.getOpponent(uid);

    if (!opponent) {
      return;
    }

    const memberDevice = opponent.memberDevice;
    const hasOpponentLeft = opponent.hasLeft;

    if (hasOpponentLeft && !memberDevice) {
      this.call.opponents.deleteOpponent(uid);
      return;
    }

    if (!memberDevice || !memberDevice.uid || !memberDevice.deviceId) {
      return;
    }

    const member = memberDevice.member;
    const hasMemberLeft = memberDevice.providerState === CallMemberStatus.Left;

    if (!hasOpponentLeft || !hasMemberLeft) {
      return;
    }

    this.call.opponents.deleteOpponent(memberDevice.uid);
    member.deleteDevice(memberDevice.deviceId);

    if (member.devices.size === 0) {
      this.handlers.addEvent({
        userId: member.userId,
        type: CallEventTypes.Finished,
      });
    }
  }

  updateResolution(): void {
    const resolution = this.call.opponents.opponentsWithVideo.length > 1 ? 1 : 0;

    this.call.opponents.opponentsWithVideo.forEach((opponent) => {
      this.handlers.changeVideoStreamQuality(
        opponent.uid,
        opponent.uid !== this.call.activeOpponentUid && Boolean(resolution)
      );
    });
  }

  addOpponentToCall(data: IAddOpponentToCallParams): void {
    const uid = data.uid;
    const opponent = this.call.opponents.getOpponent(uid);

    if (!opponent || opponent.role === ClientRole.Audience) {
      const opponent = new Opponent({
        uid,
        call: this.call,
        hasAudio: data.hasAudio,
        hasVideo: data.hasVideo,
        sortIndex: this.call.sortIndex++,
        role: data.opponentRole,
      });

      this.call.opponents.addOpponent(uid, opponent);

      if (opponent.isSharedScreen) {
        this.call.viewState.setActiveOpponent(opponent);
        this.updateResolution();
      }
    }
  }
}
